import React, { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { pendingFollows } from '../lib/discovery';
import { followOnAppview, scanAccount, unfollowOnAppview } from '../lib/scan';
import Results, { describeBatch } from './Results';

/**
 * Scan form + results. `readOnly` runs the same discovery without any write
 * controls (used by the signed-out preview).
 */
export default function Scanner({ readOnly = false }) {
  const { agent, me } = useAuth();
  const [target, setTarget] = useState('');
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [busy, setBusy] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [batch, setBatch] = useState(null);
  const [onlyProfiles, setOnlyProfiles] = useState(true);

  const rows = useMemo(() => result?.rows || [], [result]);
  const pending = useMemo(() => pendingFollows(rows), [rows]);

  const visibleRows = useMemo(
    () => (onlyProfiles ? rows.filter((row) => row.profile) : rows),
    [rows, onlyProfiles],
  );

  const runScan = async (event) => {
    event.preventDefault();

    if (!target.trim() || scanning) return;
    setScanning(true);
    setScanError(null);
    setResult(null);
    setBatch(null);
    setRowErrors({});

    try {
      const scanned = await scanAccount({
        me: me ? { did: me.did, pds: me.pds } : null,
        target,
      });

      setResult(scanned);
    } catch (err) {
      console.error('Scan failed:', err);
      setScanError(
        `Could not read that account (${err?.message || 'unknown error'}). Check the handle and try again. Some servers refuse reads from a browser, which no client can work around.`,
      );
    } finally {
      setScanning(false);
    }
  };

  const setRowBusy = (authority, value) =>
    setBusy((prev) => {
      const next = { ...prev };

      if (value) next[authority] = value;
      else delete next[authority];

      return next;
    });

  const clearRowError = (authority) =>
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[authority];

      return next;
    });

  const setRowError = (authority, message) =>
    setRowErrors((prev) => ({ ...prev, [authority]: message }));

  const patchRow = (authority, patch) =>
    setResult((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((row) => (row.authority === authority ? { ...row, ...patch } : row)),
          }
        : prev,
    );

  const doFollow = async (row) => {
    if (!agent || !me) return { ok: false };
    setRowBusy(row.authority, 'follow');
    clearRowError(row.authority);

    try {
      const created = await followOnAppview({
        agent,
        repo: me.did,
        row,
        targetDid: result.account.did,
      });

      patchRow(row.authority, { state: 'following', isFollowing: true, existing: created });

      return { ok: true };
    } catch (err) {
      console.error(`Follow failed on ${row.authority}:`, err);
      setRowError(
        row.authority,
        `Follow failed: ${err?.message || 'the server rejected the record'}. Nothing was written; try again.`,
      );

      return { ok: false };
    } finally {
      setRowBusy(row.authority, null);
    }
  };

  const doUnfollow = async (row) => {
    if (!agent || !me) return;
    setRowBusy(row.authority, 'unfollow');
    clearRowError(row.authority);

    try {
      await unfollowOnAppview({ agent, repo: me.did, row });
      patchRow(row.authority, { state: 'available', isFollowing: false, existing: null });
    } catch (err) {
      console.error(`Unfollow failed on ${row.authority}:`, err);
      setRowError(
        row.authority,
        `Could not remove the follow: ${err?.message || 'the server rejected the request'}.`,
      );
    } finally {
      setRowBusy(row.authority, null);
    }
  };

  const followAll = async () => {
    if (batch && !batch.finished) return;
    const queue = pendingFollows(result.rows);
    setBatch({ total: queue.length, done: 0, failed: 0 });
    let done = 0;
    let failed = 0;

    for (const row of queue) {
      // eslint-disable-next-line no-await-in-loop
      const outcome = await doFollow(row);

      if (outcome?.ok) done += 1;
      else failed += 1;
      setBatch({ total: queue.length, done: done + failed, failed });
    }

    setBatch({ total: queue.length, done: queue.length, failed, finished: true });
  };

  const statusText = scanning
    ? 'Reading the account’s repository and comparing it to the appview library…'
    : scanError
      ? ''
      : describeBatch(batch);

  return (
    <>
      <form className="scan-form" onSubmit={runScan}>
        <label className="field" htmlFor="scan-target">
          <span className="field-label">Account to follow</span>
          <input
            id="scan-target"
            className="input"
            type="text"
            name="target"
            placeholder="handle or did:plc:…"
            value={target}
            onChange={(event) => setTarget(event.target.value)}
            disabled={scanning}
            spellCheck="false"
            autoCapitalize="none"
          />
        </label>
        <button type="submit" disabled={scanning || !target.trim()}>
          {scanning ? 'Scanning…' : 'Scan account'}
        </button>
      </form>

      <p className="status" role="status" aria-live="polite">
        {statusText}
      </p>

      {scanError && (
        <div className="notice notice-error" role="alert">
          {scanError}
        </div>
      )}

      {result && (
        <Results
          result={result}
          visibleRows={visibleRows}
          onlyProfiles={onlyProfiles}
          onToggleOnlyProfiles={(event) => setOnlyProfiles(event.target.checked)}
          pending={pending}
          onFollowAll={followAll}
          onFollow={doFollow}
          onUnfollow={doUnfollow}
          busy={busy}
          rowErrors={rowErrors}
          batch={batch}
          readOnly={readOnly}
        />
      )}
    </>
  );
}
